import React, { useState } from "react";
import { Modal } from "react-bootstrap";

// Shared "Choose Ticket Option" block shown at the top of the Purchase/Reserve panels.
// Lets the buyer pick Single Day (the existing single-event flow) or a multi-event package,
// and open a "View details" popup for any package. It only reports the selected key; each
// panel computes its own totals (people count differs).

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  // Date-only model: events are stored at UTC midnight — format in UTC so the day doesn't shift.
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

// Date parts for the calendar-style badge in the details popup.
const datePart = (d, opts) => (d ? new Date(d).toLocaleDateString("en-US", { ...opts, timeZone: "UTC" }) : "");

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m ?? 0).padStart(2, "0")} ${ampm}`;
};

const timeRange = (ev) => {
  const s = fmtTime(ev?.start_time);
  const e = fmtTime(ev?.end_time);
  if (s && e) return `${s} - ${e}`;
  return s || e || "";
};

const money = (n) => `$${Number(n || 0)}`;

// Price cell: Free when 0, else the offer price with the (higher) total struck through + saving.
function PriceCell({ free, offer, total, save }) {
  if (free) return <span className="fw-semibold text-success">Free</span>;
  const showStrike = Number(total) > Number(offer);
  return (
    <span className="d-inline-flex flex-column align-items-end">
      <span>
        {showStrike && (
          <span className="text-muted text-decoration-line-through me-1">{money(total)}</span>
        )}
        <strong>{money(offer)}</strong>
      </span>
      {Number(save) > 0 && <small className="text-success">Save {money(save)}</small>}
    </span>
  );
}

// Per-package pricing resolved for the current viewer (member sees Free when offer is 0).
const pkgPricing = (pkg, isMember) => ({
  offer: isMember ? pkg.offer_price_member : pkg.offer_price_nonmember,
  total: isMember ? pkg.total_price_member : pkg.total_price_nonmember,
  save: isMember ? pkg.save_member : pkg.save_nonmember,
  free: isMember ? Number(pkg.offer_price_member || 0) === 0 : false,
});

function ChooseTicketOption({ event, packages = [], isMember = false, selectedKey, onSelect, wrapperClassName = "px-4 pt-3" }) {
  const [detailsPkg, setDetailsPkg] = useState(null);

  if (!packages || packages.length === 0) return null; // No packages → keep the panel as-is.

  const eventDateLabel = event?.start_date ? `${fmtDate(event.start_date)} only · 1 ticket` : "1 ticket";
  const singleFree = isMember ? Number(event?.member_price || 0) === 0 : false;

  const Row = ({ value, title, subtitle, priceNode, onViewDetails }) => {
    const active = selectedKey === value;
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(value)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(value)}
        className="w-100 d-flex justify-content-between align-items-start"
        style={{
          border: `1px solid ${active ? "#F4C04A" : "var(--border-color)"}`,
          background: active ? "rgba(237,169,43,0.12)" : "#fff",
          borderRadius: 8,
          padding: "10px 12px",
          gap: 8,
          marginBottom: 8,
          cursor: "pointer",
        }}
      >
        <span className="d-flex align-items-start gap-2">
          <input type="radio" checked={active} readOnly style={{ marginTop: 4 }} />
          <span>
            <span className="d-block fw-semibold text-main">{title}</span>
            <small className="text-muted">{subtitle}</small>
            {onViewDetails && (
              <button
                type="button"
                className="d-block btn btn-link p-0 mt-1 text-decoration-none"
                style={{ color: "#E69F00", fontSize: 13, fontWeight: 600 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails();
                }}
              >
                View details ›
              </button>
            )}
          </span>
        </span>
        <span className="text-end">{priceNode}</span>
      </div>
    );
  };

  return (
    <div className={wrapperClassName}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h2 className="f-16 fw-semibold text-main mb-0">Choose Ticket Option</h2>
        <span className="badge bg-light text-muted">NEW</span>
      </div>

      <Row
        value="single"
        title="Single Day"
        subtitle={eventDateLabel}
        priceNode={
          <PriceCell free={singleFree} offer={isMember ? event?.member_price : event?.price} total={0} save={0} />
        }
      />

      {packages.map((pkg) => {
        const datesLabel = (pkg.included_events || []).map((e) => fmtDate(e.start_date)).join(", ");
        const { offer, total, save, free } = pkgPricing(pkg, isMember);
        return (
          <Row
            key={pkg._id}
            value={pkg._id}
            title={pkg.name}
            subtitle={`${datesLabel} · ${pkg.event_count} tickets`}
            priceNode={<PriceCell free={free} offer={offer} total={total} save={save} />}
            onViewDetails={() => setDetailsPkg(pkg)}
          />
        );
      })}

      {/* ---- Package details popup ---- */}
      <Modal show={!!detailsPkg} onHide={() => setDetailsPkg(null)} centered>
        {detailsPkg && (() => {
          const { offer, total, save, free } = pkgPricing(detailsPkg, isMember);
          const events = detailsPkg.included_events || [];
          return (
            <Modal.Body className="p-4">
              {/* Header */}
              <div className="d-flex align-items-start justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      width: 44, height: 44, borderRadius: "50%",
                      background: "rgba(237,169,43,0.12)", border: "1px solid #F4C04A",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      color: "#E69F00", fontSize: 20,
                    }}
                  >
                    🎟
                  </span>
                  <div>
                    <div className="fw-bold text-main f-20">{detailsPkg.name}</div>
                    {detailsPkg.description && <small className="text-muted">{detailsPkg.description}</small>}
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setDetailsPkg(null)} aria-label="Close" />
              </div>

              {/* Price card */}
              <div style={{ background: "rgba(237,169,43,0.08)", border: "1px solid #F4D79A", borderRadius: 12, padding: 16 }} className="mb-3">
                <small className="text-muted d-block mb-1">Price per person</small>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-baseline gap-2">
                    {free ? (
                      <span className="fw-bold text-success f-20">Free</span>
                    ) : (
                      <>
                        <span className="fw-bold text-main" style={{ fontSize: 28 }}>{money(offer)}</span>
                        {Number(total) > Number(offer) && (
                          <span className="text-muted text-decoration-line-through">{money(total)}</span>
                        )}
                      </>
                    )}
                  </div>
                  {Number(save) > 0 && (
                    <span className="badge rounded-pill" style={{ background: "rgba(40,167,69,0.12)", color: "#1e7e34", fontWeight: 600 }}>
                      Save {money(save)}
                    </span>
                  )}
                </div>
                <hr className="my-2" />
                <div className="d-flex align-items-center gap-2 text-main">
                  <span>🎟</span>
                  <small>Each person gets {detailsPkg.event_count} tickets, one for each event.</small>
                </div>
              </div>

              {/* Included events */}
              <div className="fw-semibold text-main mb-2">Included events ({events.length})</div>
              <div className="mb-3">
                {events.map((ev, i) => {
                  const isThis = event?._id && ev._id === event._id;
                  return (
                    <div key={ev._id} className="d-flex gap-3 mb-3">
                      <div
                        style={{
                          width: 56, textAlign: "center", borderRadius: 8,
                          border: "1px solid #F4C04A", overflow: "hidden", flexShrink: 0,
                        }}
                      >
                        <div style={{ background: "#F4C04A", color: "#fff", fontSize: 11, fontWeight: 600, padding: "1px 0" }}>
                          {datePart(ev.start_date, { month: "short" })}
                        </div>
                        <div className="fw-bold text-main" style={{ fontSize: 18, lineHeight: 1.2, padding: "2px 0" }}>
                          {datePart(ev.start_date, { day: "numeric" })}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {datePart(ev.start_date, { weekday: "short" })}
                        </div>
                      </div>
                      <div>
                        <div className="fw-semibold text-main">{ev.title}</div>
                        {timeRange(ev) && (
                          <small className="text-muted d-flex align-items-center gap-1">🕒 {timeRange(ev)}</small>
                        )}
                        {isThis && (
                          <span className="badge rounded-pill mt-1" style={{ background: "rgba(237,169,43,0.15)", color: "#E69F00", fontWeight: 600 }}>
                            This event
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                className="btn btn-orange-md w-100"
                onClick={() => {
                  onSelect(detailsPkg._id);
                  setDetailsPkg(null);
                }}
              >
                Choose this option
              </button>
            </Modal.Body>
          );
        })()}
      </Modal>
    </div>
  );
}

export default ChooseTicketOption;
